import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import SmsIcon from "@mui/icons-material/Sms";
import CreditScoreIcon from "@mui/icons-material/CreditScore";
import PublicIcon from "@mui/icons-material/Public";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { acme, acmeMarketingSectionTitleSx, acmeMarketingBodyLeadSx } from "../../theme/acmeTheme";
import { Card, CardContent, Table, Input, Button, Chip } from "../ui";

import { canSeeSmsProviderPricing } from "../../utils/userRoles";

const smsStatCardSx = {
  p: 2.5,
  height: "100%",
  borderColor: acme.colors.border,
  background: `linear-gradient(135deg, ${acme.colors.brandMuted} 0%, ${acme.colors.background} 100%)`,
  boxShadow: "none",
};

function formatMoney(value, currency = "EUR") {
  if (value == null || Number.isNaN(Number(value))) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: String(currency || "EUR").toUpperCase(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(Number(value));
  } catch {
    return `${Number(value).toFixed(4)} ${currency || ""}`.trim();
  }
}

function PricingSms({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pricing, setPricing] = useState(null);
  const [search, setSearch] = useState("");

  const showProviderPricing =
    pricing?.showProviderPricing ?? canSeeSmsProviderPricing(user?.role);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await axios.get("/api/sms/pricing", {
          withCredentials: true,
        });
        if (!cancelled) setPricing(data);
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.message ||
              err.response?.data?.error ||
              "Could not load SMS pricing.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredRates = useMemo(() => {
    const rates = pricing?.rates || [];
    const q = search.trim().toLowerCase();
    if (!q) return rates;
    return rates.filter(
      (row) =>
        row.countryName?.toLowerCase().includes(q) ||
        row.countryCode?.toLowerCase().includes(q),
    );
  }, [pricing, search]);

  const markupPercent = pricing?.markupPercent ?? 130;

  return (
    <Box>
      <Card sx={{ mb: 4, boxShadow: "none" }}>
        <CardContent sx={{ p: 4 }}>
        <Typography
          component="h2"
          gutterBottom
          textAlign="center"
          sx={{ ...acmeMarketingSectionTitleSx, mb: 1, fontSize: "1.25rem" }}
        >
          SMS Campaign Pricing
        </Typography>
        <Typography
          textAlign="center"
          sx={{
            ...acmeMarketingBodyLeadSx,
            color: acme.colors.mutedForegroundSolid,
            maxWidth: 760,
            mx: "auto",
            mb: 3,
          }}
        >
          {showProviderPricing
            ? `Live rates from BulkGate for promotional SMS. Your price includes a ${markupPercent}% platform margin. Campaigns are billed in SMS credits — the number of credits per segment depends on the destination country.`
            : "Per-segment SMS rates by destination country. Campaigns are billed in SMS credits — the number of credits per segment depends on the destination country."}
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="warning">{error}</Alert>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={showProviderPricing ? 4 : 6}>
                <Card sx={smsStatCardSx}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <CreditScoreIcon sx={{ color: acme.colors.brand }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Credits
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, color: "#2c3e50" }}
                  >
                    1 credit ≈ {formatMoney(pricing.creditValueEur, "EUR")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    credits per SMS segment vary by destination (see table
                    below)
                  </Typography>
                </Card>
              </Grid>
              {showProviderPricing && (
                <Grid item xs={12} md={4}>
                  <Card sx={smsStatCardSx}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <PublicIcon sx={{ color: acme.colors.brand }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Platform margin
                      </Typography>
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 700, color: acme.colors.foreground }}
                    >
                      +{markupPercent}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      applied to BulkGate route price shown below
                    </Typography>
                  </Card>
                </Grid>
              )}
              <Grid item xs={12} md={showProviderPricing ? 4 : 6}>
                <Card sx={smsStatCardSx}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <SmsIcon sx={{ color: acme.colors.brand }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Sender setup
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Label campaigns only
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {pricing.numberSetupFeeCredits > 0
                      ? `Number setup: ${pricing.numberSetupFeeCredits} credits. `
                      : ""}
                    {pricing.numberMonthlyFeeCredits > 0
                      ? `Monthly hosting: ${pricing.numberMonthlyFeeCredits} credits.`
                      : "Verify your company label in SMS Phones before sending."}
                  </Typography>
                </Card>
              </Grid>
            </Grid>

            <Alert icon={<InfoOutlinedIcon />} severity="info" sx={{ mb: 3 }}>
              {(pricing.notes || []).map((note) => (
                <Typography
                  key={note}
                  variant="body2"
                  sx={{ mb: note === pricing.notes.at(-1) ? 0 : 0.5 }}
                >
                  {note}
                </Typography>
              ))}
              {pricing.updatedAt && (
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ mt: 1, opacity: 0.8 }}
                >
                  Rates updated {new Date(pricing.updatedAt).toLocaleString()}
                  {pricing.cached ? " (cached)" : ""}
                </Typography>
              )}
            </Alert>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Input
                label="Search country"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: 240 }}
              />
              <Button variant="default" onClick={() => navigate("/sms/campaign")}>
                Create SMS campaign
              </Button>
            </Box>

            <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Country</TableCell>
                    {showProviderPricing && (
                      <TableCell sx={{ fontWeight: 700 }}>
                        BulkGate cost / segment
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 700 }}>
                      Credits / segment
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      Price / segment
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRates.map((row) => (
                    <TableRow key={row.countryCode} hover>
                      <TableCell>
                        <Typography sx={{ fontWeight: 600 }}>
                          {row.countryName}
                        </Typography>
                        <Chip
                          label={row.countryCode}
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      </TableCell>
                      {showProviderPricing && (
                        <TableCell>
                          {formatMoney(
                            row.providerCostEurPerSegment,
                            "EUR",
                          )}
                        </TableCell>
                      )}
                      <TableCell sx={{ color: acme.colors.brand, fontWeight: 700 }}>
                        {row.creditsPerSegment != null
                          ? `${row.creditsPerSegment.toLocaleString()} cr`
                          : "—"}
                      </TableCell>
                      <TableCell sx={{ color: acme.colors.brand, fontWeight: 700 }}>
                        ≈ {formatMoney(row.eurPerSegment, "EUR")}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredRates.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={showProviderPricing ? 4 : 3}
                        align="center"
                        sx={{ py: 4 }}
                      >
                        No countries match your search.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
            </Table>
          </>
        )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default PricingSms;
